import type { MetricCanonicalName, SetPlanInfo, SetPlanInfoId, StepPlanInfoId } from '@az3fr/core';
import { createID, metricToColumnMap } from '@az3fr/core';
import type { PayloadAction } from '@reduxjs/toolkit';
import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';

import { stepPlanSliceActions } from '@/entities/step-plan/model/step-plan.slice';

// Factory Function (The blueprint for a new set plan)
function createNewSetPlan(payload: { stepPlanId: StepPlanInfoId; setNumber: number }): SetPlanInfo {
	const { stepPlanId, setNumber } = payload;
	const now = new Date().toISOString();
	return {
		id: createID('setPlan'),
		stepPlanId,
		setNumber,
		targetWeightMetric: null,
		targetRepsMetric: null,
		targetDistanceMetric: null,
		targetDurationMetric: null,
		targetRepPaceMetric: null,
		targetHeightMetric: null,
		trackTime: false,
		updatedAt: now,
	};
}

const setPlanAdapter = createEntityAdapter<SetPlanInfo>({
	sortComparer: (a, b) => a.setNumber - b.setNumber,
});
const setPlanSlice = createSlice({
	name: 'entities/setPlan',
	initialState: setPlanAdapter.getInitialState(),
	reducers: {
		metricValueUpdated: (
			state,
			action: PayloadAction<{
				setPlanId: SetPlanInfoId;
				metricCanonicalName: MetricCanonicalName;
				value: number;
			}>,
		) => {
			const { setPlanId, metricCanonicalName, value } = action.payload;
			const metricValueKey = metricToColumnMap[metricCanonicalName].targetColumn;
			if (!metricValueKey) {
				return;
			}

			const setPlan = state.entities[setPlanId];
			if (!setPlan) {
				return;
			}

			setPlanAdapter.updateOne(state, {
				id: setPlanId,
				changes: {
					[metricValueKey]: value,
					updatedAt: new Date().toISOString(),
				},
			});
		},

		addedOne: setPlanAdapter.addOne,
		addedMany: setPlanAdapter.addMany,

		updatedOne: setPlanAdapter.updateOne,
		updatedMany: setPlanAdapter.updateMany,

		removedOne: setPlanAdapter.removeOne,
		removedMany: setPlanAdapter.removeMany,
	},

	extraReducers: (builder) => {
		builder.addCase(stepPlanSliceActions.metricUntracked, (state, action) => {
			const { id: stepPlanId, metricCanonicalName } = action.payload;

			// Find the set plan for this step plan
			const setPlan = Object.values(state.entities).find(
				(setPlan: SetPlanInfo) => setPlan.stepPlanId === stepPlanId,
			);

			if (!setPlan) {
				return;
			}

			const metricValueKey = metricToColumnMap[metricCanonicalName].targetColumn;
			if (!metricValueKey) {
				return;
			}

			// Clear the target metric value when metric is removed from tracking
			if (metricValueKey in setPlan) {
				setPlanAdapter.updateOne(state, {
					id: setPlan.id,
					changes: {
						[metricValueKey]: null,
						updatedAt: new Date().toISOString(),
					},
				});
			}
		});
	},
});
type SetPlanSliceState = ReturnType<typeof setPlanAdapter.getInitialState>;

const setPlanSliceActions = { ...setPlanSlice.actions };
const setPlanSliceReducer = setPlanSlice.reducer;

export { createNewSetPlan, setPlanSliceActions, setPlanSliceReducer, setPlanAdapter };
export type { SetPlanSliceState };
